import React, { useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectItem,
} from "@/components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "@/components/ui/dialog";

import {
	Upload,
	FileText,
	Eye,
	X,
	Edit3,
	Check,
	BookOpen,
	Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
	OrgaAdminKlausurService,
	type Klausur,
} from "@/lib/orgaAdmin/orgaAdminKlausur.service";
import type { OrgaAdminGruppeDTO } from "@/lib/orgaAdmin/orgaAdmin.types";

export interface UploadedFile {
	id: string;
	file: File;
	originalName: string;
	displayName: string;
	extractedId: string | null;
	selectedKlausurId: string;
	previewUrl: string | null;
	isEditing: boolean;
}

interface MassUploadStudentKlausurenProps {
	klausurs: Klausur[];
	groups: OrgaAdminGruppeDTO[];
	selectedOrganizationId: string;
	selectedGruppeId: string;
	onGruppeChange: (gruppeId: string) => void;
	isLoadingKlausurs: boolean;
	isLoadingGroups: boolean;
	onUploadComplete: () => void;
	onFilesChanged?: (hasFiles: boolean) => void;
	defaultMode?: "single" | "mass";
}

interface PDFPreviewDialogProps {
	file: File | null;
	isOpen: boolean;
	onClose: () => void;
}

function PDFPreviewDialog({ file, isOpen, onClose }: PDFPreviewDialogProps) {
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	React.useEffect(() => {
		if (file && isOpen) {
			const url = URL.createObjectURL(file);
			setPreviewUrl(url);
			return () => {
				URL.revokeObjectURL(url);
				setPreviewUrl(null);
			};
		}
	}, [file, isOpen]);

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] p-0 flex flex-col">
				<DialogHeader className="px-6 py-4">
					<DialogTitle>PDF Vorschau: {file?.name}</DialogTitle>
					<DialogDescription>
						Vorschau der ausgewählten PDF-Datei
					</DialogDescription>
				</DialogHeader>
				<div className="flex-1 overflow-hidden">
					{previewUrl ? (
						<iframe
							src={previewUrl}
							className="w-full h-full"
							title="PDF Preview"
						/>
					) : (
						<div className="flex items-center justify-center h-full">
							<div className="text-center">
								<FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
								<p className="text-muted-foreground">
									PDF-Vorschau wird geladen...
								</p>
							</div>
						</div>
					)}
				</div>
				<DialogFooter className="px-6 py-4 border-t">
					<Button variant="outline" onClick={onClose}>
						Schließen
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export default function MassUploadStudentKlausuren({
	klausurs,
	groups,
	selectedOrganizationId,
	selectedGruppeId,
	onGruppeChange,
	isLoadingKlausurs,
	isLoadingGroups,
	onUploadComplete,
	onFilesChanged,
}: MassUploadStudentKlausurenProps) {
	const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [previewFile, setPreviewFile] = useState<File | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [isDragOver, setIsDragOver] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Notify parent when files change
	React.useEffect(() => {
		onFilesChanged?.(uploadedFiles.length > 0);
	}, [uploadedFiles.length, onFilesChanged]);

	const processFiles = useCallback(
		(files: FileList) => {
			const newFiles: UploadedFile[] = [];
			const defaultKlausurId = klausurs.length > 0 ? klausurs[0].id : "";

			for (let i = 0; i < files.length; i++) {
				const file = files[i];

				// Validate file type
				if (file.type !== "application/pdf") {
					toast.error(`${file.name} ist keine PDF-Datei und wird übersprungen`);
					continue;
				}

				const uploadedFile: UploadedFile = {
					id: `${Date.now()}_${i}`,
					file,
					originalName: file.name,
					displayName: file.name,
					extractedId: null,
					selectedKlausurId: defaultKlausurId,
					previewUrl: null,
					isEditing: false,
				};

				newFiles.push(uploadedFile);
			}

			setUploadedFiles((prev) => [...prev, ...newFiles]);
			toast.success(`${newFiles.length} Dateien hinzugefügt`);
		},
		[klausurs],
	);

	const handleFileSelection = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const files = event.target.files;
			if (!files) return;

			processFiles(files);

			// Clear file input to allow selecting the same files again
			event.target.value = "";
			if (fileInputRef.current) fileInputRef.current.value = "";
		},
		[processFiles],
	);
	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragOver(false);

			const files = e.dataTransfer.files;
			if (files.length > 0) {
				processFiles(files);
			}
		},
		[processFiles],
	);

	const handleRemoveFile = useCallback((fileId: string) => {
		setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
	}, []);

	const handleEditName = useCallback((fileId: string) => {
		setUploadedFiles((prev) =>
			prev.map((f) => (f.id === fileId ? { ...f, isEditing: true } : f)),
		);
	}, []);

	const handleSaveName = useCallback((fileId: string, newName: string) => {
		setUploadedFiles((prev) =>
			prev.map((f) =>
				f.id === fileId ? { ...f, displayName: newName, isEditing: false } : f,
			),
		);
	}, []);

	const handleCancelEdit = useCallback((fileId: string) => {
		setUploadedFiles((prev) =>
			prev.map((f) => (f.id === fileId ? { ...f, isEditing: false } : f)),
		);
	}, []);

	const handleKlausurChange = useCallback(
		(fileId: string, klausurId: string) => {
			setUploadedFiles((prev) =>
				prev.map((f) =>
					f.id === fileId ? { ...f, selectedKlausurId: klausurId } : f,
				),
			);
		},
		[],
	);

	const handlePreview = useCallback((file: File) => {
		setPreviewFile(file);
		setIsPreviewOpen(true);
	}, []);

	const handleUploadAll = useCallback(async () => {
		if (!selectedGruppeId) {
			toast.error("Bitte wählen Sie eine Gruppe aus");
			return;
		}

		if (uploadedFiles.length === 0) {
			toast.error("Keine Dateien zum Hochladen ausgewählt");
			return;
		}

		// Validate that all files have a selected klausur
		const filesWithoutKlausur = uploadedFiles.filter(
			(f) => !f.selectedKlausurId,
		);
		if (filesWithoutKlausur.length > 0) {
			toast.error("Alle Dateien müssen einer Klausur zugeordnet werden");
			return;
		}

		setIsUploading(true);

		try {
			const service = OrgaAdminKlausurService.getInstance();

			let successCount = 0;
			let errorCount = 0;

			// Upload files one by one to provide better feedback
			for (const uploadedFile of uploadedFiles) {
				try {
					const internalIdentifier = uploadedFile.displayName;

					await service.createStudentKlausur(
						selectedOrganizationId,
						uploadedFile.selectedKlausurId,
						uploadedFile.file,
						internalIdentifier,
						selectedGruppeId,
					);

					successCount++;
				} catch (error) {
					console.error(`Error uploading ${uploadedFile.displayName}:`, error);
					errorCount++;
				}
			}

			if (successCount > 0) {
				toast.success(
					`${successCount} Studentenklausuren erfolgreich erstellt`,
				);
				setUploadedFiles([]);
				onUploadComplete();
			}

			if (errorCount > 0) {
				toast.error(`${errorCount} Dateien konnten nicht hochgeladen werden`);
			}
		} catch (error) {
			console.error("Error during mass upload:", error);
			toast.error("Fehler beim Hochladen der Dateien");
		} finally {
			setIsUploading(false);
		}
	}, [
		uploadedFiles,
		selectedGruppeId,
		selectedOrganizationId,
		onUploadComplete,
	]);

	return (
		<div className="w-full space-y-6">
			{renderUploadForm()}

			{/* PDF Preview Dialog */}
			<PDFPreviewDialog
				file={previewFile}
				isOpen={isPreviewOpen}
				onClose={() => setIsPreviewOpen(false)}
			/>
		</div>
	);

	function renderUploadForm() {
		const selectedGroup = groups.find((g) => g.id === selectedGruppeId);

		return (
			<div className="space-y-6">
				{/* Group Selection */}
				<div className="space-y-2">
					<Label>Gruppe auswählen</Label>
					<Select
						value={selectedGruppeId}
						onValueChange={onGruppeChange}
						disabled={isLoadingGroups}
					>
						<SelectTrigger>
							<SelectValue
								placeholder={
									isLoadingGroups
										? "Gruppen werden geladen..."
										: "Gruppe auswählen..."
								}
							/>
						</SelectTrigger>
						<SelectContent>
							{groups.map((group) => (
								<SelectItem key={group.id} value={group.id}>
									<div className="flex items-center gap-2">
										<span>{group.title}</span>
										<span className="text-xs text-muted-foreground">
											({group.users.length} Benutzer*innen)
										</span>
									</div>
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{selectedGroup && (
						<p className="text-sm text-muted-foreground">
							Ausgewählte Gruppe: {selectedGroup.title} (
							{selectedGroup.users.length} Benutzer*innen,{" "}
							{selectedGroup.studentKlausuren.length} bestehende
							Studentenklausuren)
						</p>
					)}
				</div>

				{/* File Upload */}
				<div className="space-y-2">
					<Label>PDF-Dateien auswählen</Label>
					<button
						type="button"
						className={`relative w-full border-2 border-dashed rounded-lg p-8 transition-colors cursor-pointer ${
							isDragOver
								? "border-blue-400 bg-blue-50"
								: "border-gray-300 hover:border-gray-400"
						}`}
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
						onDrop={handleDrop}
						aria-label="Upload PDF files"
					>
						<Input
							ref={fileInputRef}
							type="file"
							accept=".pdf,application/pdf"
							multiple
							onChange={handleFileSelection}
							className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
						/>
						<div className="flex flex-col items-center gap-3">
							<div
								className={`p-3 rounded-full ${isDragOver ? "bg-blue-100" : "bg-slate-100"}`}
							>
								<Upload
									className={`h-6 w-6 ${isDragOver ? "text-blue-600" : "text-slate-600"}`}
								/>
							</div>
							<div className="text-center">
								<p className="text-sm font-medium text-slate-900">
									{isDragOver
										? "PDF-Dateien hier ablegen"
										: "PDF-Dateien hochladen"}
								</p>
								<p className="text-xs text-slate-500 mt-1">
									Dateien hierher ziehen oder klicken zum Auswählen
								</p>
								<p className="text-xs text-slate-400 mt-1">
									Unterstützte Formate: PDF-Dateien
								</p>
							</div>
						</div>
					</button>
				</div>

				{/* Uploaded Files List */}
				{uploadedFiles.length > 0 && (
					<div className="space-y-4">
						<div className="flex items-center justify-between">
							<h3 className="text-lg font-medium">
								Hochgeladene Dateien ({uploadedFiles.length})
							</h3>
							<Button
								onClick={handleUploadAll}
								disabled={
									isUploading ||
									!selectedGruppeId ||
									uploadedFiles.some((f) => !f.selectedKlausurId)
								}
								className="ml-auto"
							>
								{isUploading ? (
									<div className="flex items-center gap-2">
										<div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
										Wird hochgeladen...
									</div>
								) : (
									<div className="flex items-center gap-2">
										<Upload className="h-4 w-4" />
										{uploadedFiles.length === 1
											? "Hochladen"
											: "Alle hochladen"}
									</div>
								)}
							</Button>
						</div>

						<div className="space-y-2">
							{uploadedFiles.map((uploadedFile, index) => (
								<FileUploadItem
									key={uploadedFile.id}
									uploadedFile={uploadedFile}
									index={index + 1}
									klausurs={klausurs}
									isLoadingKlausurs={isLoadingKlausurs}
									onRemove={handleRemoveFile}
									onEditName={handleEditName}
									onSaveName={handleSaveName}
									onCancelEdit={handleCancelEdit}
									onKlausurChange={handleKlausurChange}
									onPreview={handlePreview}
								/>
							))}
						</div>
					</div>
				)}
			</div>
		);
	}
}

interface FileUploadItemProps {
	uploadedFile: UploadedFile;
	index: number;
	klausurs: Klausur[];
	isLoadingKlausurs: boolean;
	onRemove: (fileId: string) => void;
	onEditName: (fileId: string) => void;
	onSaveName: (fileId: string, newName: string) => void;
	onCancelEdit: (fileId: string) => void;
	onKlausurChange: (fileId: string, klausurId: string) => void;
	onPreview: (file: File) => void;
}

function FileUploadItem({
	uploadedFile,
	index,
	klausurs,
	isLoadingKlausurs,
	onRemove,
	onEditName,
	onSaveName,
	onCancelEdit,
	onKlausurChange,
	onPreview,
}: FileUploadItemProps) {
	const [editName, setEditName] = useState(uploadedFile.displayName);

	const handleSave = () => {
		onSaveName(uploadedFile.id, editName);
	};

	const handleCancel = () => {
		setEditName(uploadedFile.displayName);
		onCancelEdit(uploadedFile.id);
	};

	return (
		<Card className="p-4">
			<div className="flex items-start gap-4 min-w-0">
				<div className="flex items-center gap-3 flex-shrink-0">
					<span className="text-sm font-medium text-gray-500 bg-gray-100 rounded-full w-6 h-6 flex items-center justify-center">
						{index}
					</span>
					<FileText className="h-8 w-8 text-blue-500" />
				</div>

				<div className="flex-1 space-y-3 min-w-0">
					{/* File Name */}
					<div className="space-y-1">
						{uploadedFile.isEditing ? (
							<div className="flex items-center gap-2">
								<Input
									value={editName}
									onChange={(e) => setEditName(e.target.value)}
									className="flex-1"
									placeholder="Dateiname eingeben..."
								/>
								<Button size="sm" onClick={handleSave}>
									<Check className="h-4 w-4" />
								</Button>
								<Button size="sm" variant="outline" onClick={handleCancel}>
									<X className="h-4 w-4" />
								</Button>
							</div>
						) : (
							<div className="flex items-center gap-2">
								<h4 className="font-medium truncate">
									{uploadedFile.displayName}
								</h4>
								<Button
									size="sm"
									variant="ghost"
									onClick={() => onEditName(uploadedFile.id)}
								>
									<Edit3 className="h-4 w-4" />
								</Button>
							</div>
						)}
						<p className="text-sm text-muted-foreground truncate">
							Original: {uploadedFile.originalName} (
							{(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB)
						</p>
					</div>

					{/* Klausur Selection */}
					<div className="space-y-1">
						<Label className="text-sm">Klausur zuordnen</Label>
						<Select
							value={uploadedFile.selectedKlausurId}
							onValueChange={(value) => onKlausurChange(uploadedFile.id, value)}
							disabled={isLoadingKlausurs}
						>
							<SelectTrigger>
								<SelectValue
									placeholder={
										isLoadingKlausurs
											? "Klausuren werden geladen..."
											: "Klausur auswählen..."
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{klausurs.map((klausur) => (
									<SelectItem key={klausur.id} value={klausur.id}>
										<div className="flex items-center gap-2">
											<BookOpen className="h-4 w-4" />
											<span>{klausur.title}</span>
											<span className="text-xs text-muted-foreground">
												(Index: {klausur.index})
											</span>
										</div>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				{/* Action Buttons */}
				<div className="flex flex-col gap-1 flex-shrink-0">
					<Button
						size="sm"
						variant="outline"
						onClick={() => onPreview(uploadedFile.file)}
						className="h-8 w-8 p-0"
					>
						<Eye className="h-4 w-4" />
					</Button>
					<Button
						size="sm"
						variant="destructive"
						onClick={() => onRemove(uploadedFile.id)}
						className="h-8 w-8 p-0"
					>
						<Trash2 className="h-4 w-4" />
					</Button>
				</div>
			</div>
		</Card>
	);
}
